import { Compass, TrendingUp, Sparkles, Shield, BarChart, BookOpen, Heart, Film } from "lucide-react";
import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import EditorialHeader from "@/components/editorial/EditorialHeader";
import EditorialFooter from "@/components/editorial/EditorialFooter";
import EditorialGridLayout from "@/components/editorial/EditorialGridLayout";
import EditorialSectionDivider from "@/components/editorial/EditorialSectionDivider";
import GridLinesWrapper from "@/components/editorial/GridLinesWrapper";
import EditorialVideoTestimonials from "@/components/editorial/EditorialVideoTestimonials";
import LatestWorkSection from "@/components/sections/LatestWorkSection";
import CalendarDialog from "@/components/dialogs/CalendarDialog";
import AnimatedBorderBadge from "@/components/editorial/AnimatedBorderBadge";

const AboutContent = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { theme } = useTheme();

  // Theme-aware colors
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const accentColor = theme === 'dark' ? '#FFFFFF' : '#111111';

  const values = [
    {
      icon: Compass,
      letter: "S",
      title: "Strategy",
      description: "Planning and intentionality; ensuring every action aligns with the long-term vision."
    },
    {
      icon: TrendingUp,
      letter: "T",
      title: "Tenacity",
      description: "Persistence and resilience; never giving up on a goal or challenge."
    },
    {
      icon: Sparkles,
      letter: "O",
      title: "Originality",
      description: "Creativity and innovation; delivering unique and fresh solutions."
    },
    {
      icon: Shield,
      letter: "R",
      title: "Reputation",
      description: "Integrity and external image; consistently acting in ways that uphold our brand and build trust."
    },
    {
      icon: BarChart,
      letter: "Y",
      title: "Yardstick",
      description: "Measurable success; using clear metrics to gauge performance and impact."
    }
  ];

  const testimonials = [
    {
      id: 1,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/YourStory%20Testimonial%20Style%20UGC%20-%20Linda%20Vittoria%20(1).mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-23%20211814.png"
    },
    {
      id: 2,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Story%20Marketing.mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-25%20202529.png"
    },
    {
      id: 3,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Time-Saving%20Platform%20Empowerment%20Story.mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20210154.png"
    },
    {
      id: 4,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/pro%20vision%20ver1.mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20154250.png"
    },
    {
      id: 5,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Fiverr_Story%20(1).mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20205432.png"
    },
    {
      id: 6,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Story_Marketing%20(2).mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-25%20202907.png"
    },
    {
      id: 7,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Testimonial%20.mov",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20171523.png"
    },
    {
      id: 8,
      videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Label2.mp4",
      thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-25%20205248.png"
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      <EditorialHeader />
      
      {/* Hero Section - Our Story */}
      <GridLinesWrapper>
        <section className="pt-32 lg:pt-40 pb-16 lg:pb-24">
          <div className="max-w-[960px] mx-auto px-8">
            <div className="text-center">
              {/* Badge */}
              <div className="flex justify-center mb-6">
                <AnimatedBorderBadge icon={BookOpen} text="About Us" />
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-[1.1] tracking-tight"
                style={{ color: headingColor }}
              >
                Our Story.
              </h1>
              
              <div className="space-y-6 text-left max-w-3xl mx-auto">
                <p style={{ color: textColor, fontSize: '15px', lineHeight: '1.7' }}>
                  Story Marketing was forged in frustration. Working with professional service firms, we realized the "single-platform" myth was costing everyone dearly. We were stuck constantly integrating complex systems that wouldn't speak to each other, resulting in painful data collation and analysis, and significant costs in money, time, and energy. We wasted significant capital trying to find an effective CRM that simply didn't exist.
                </p>
                <p style={{ color: textColor, fontSize: '15px', lineHeight: '1.7' }}>
                  That search is over. Our platform offers everything you need in one space. It's not just about better metrics or lead management; it's about giving you the tools to foster lasting client relationships and effortlessly take your business to the next level.
                </p>
              </div>
            </div>
          </div>
        </section>
      </GridLinesWrapper>

      <EditorialSectionDivider />

      {/* Values Section */}
      <GridLinesWrapper>
        <section className="py-16 lg:py-24">
          <div className="max-w-[1200px] mx-auto px-8">
            <div className="text-center mb-12">
              {/* Badge */}
              <div className="flex justify-center mb-6">
                <AnimatedBorderBadge icon={Heart} text="Our Values" />
              </div>
              <h2 
                className="text-3xl lg:text-4xl font-bold"
                style={{ color: headingColor }}
              >
                Our Values.
              </h2>
              <p className="mt-3" style={{ color: textColor, fontSize: '14px' }}>
                The principles that guide everything we do.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className="rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] group"
                  style={{ 
                    backgroundColor: cardBgColor,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto transition-colors"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: accentColor }}>{value.letter}</span>
                  </div>
                  <h3 
                    className="text-lg font-semibold mb-3 text-center"
                    style={{ color: headingColor }}
                  >
                    {value.title}
                  </h3>
                  <p 
                    className="text-sm leading-relaxed text-center"
                    style={{ color: textColor }}
                  >
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </GridLinesWrapper>

      <EditorialSectionDivider />

      {/* The Stories We're Telling Section */}
      <GridLinesWrapper>
        <section className="py-12 lg:py-16">
          <div className="max-w-[960px] mx-auto px-8">
            <div className="text-center">
              {/* Badge */}
              <div className="flex justify-center mb-6">
                <AnimatedBorderBadge icon={Film} text="Case Studies" />
              </div>
              <h2 
                className="text-3xl lg:text-4xl font-bold"
                style={{ color: headingColor }}
              >
                The Stories We're Telling.
              </h2>
              <p className="mt-3" style={{ color: textColor, fontSize: '14px' }}>
                Story's Done-For-You Marketing Services help businesses scale effortlessly.
              </p>
            </div>
          </div>
        </section>
      </GridLinesWrapper>

      {/* Our Latest Work Section */}
      <GridLinesWrapper>
        <LatestWorkSection />
      </GridLinesWrapper>

      <EditorialSectionDivider />

      {/* Success Stories Section */}
      <GridLinesWrapper>
        <EditorialVideoTestimonials testimonials={testimonials} />
      </GridLinesWrapper>

      <EditorialSectionDivider />

      {/* Footer */}
      <EditorialFooter />

      {/* Calendar Dialog */}
      <CalendarDialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen} />
    </div>
  );
};

const About = () => {
  return (
    <EditorialGridLayout>
      <AboutContent />
    </EditorialGridLayout>
  );
};

export default About;
